import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

// Get environment variables with fallback values
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://uglrgwybgedlsebnxvia.supabase.co';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVnbHJnd3liZ2VkbHNlYm54dmlhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc5NzU5NTIsImV4cCI6MjA1MzU1MTk1Mn0.W1RhHKfiHR5PrR5orvrr7UoJZqvYLKdqHgqUsmYuJ-c';

// Create a single instance of the Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage
  },
  global: {
    headers: {
      'x-client-info': 'sollbridge-web'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  // Add retry configuration
  fetch: (url, options = {}) => {
    const retryCount = 3;
    const baseDelay = 1000; // 1 second
    const maxDelay = 5000; // 5 seconds

    const fetchWithRetry = async (attempt = 0): Promise<Response> => {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok && attempt < retryCount) {
          // Calculate delay with exponential backoff and jitter
          const delay = Math.min(
            baseDelay * Math.pow(2, attempt) * (0.5 + Math.random()),
            maxDelay
          );
          
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(attempt + 1);
        }

        return response;
      } catch (error) {
        if (attempt < retryCount) {
          const delay = Math.min(
            baseDelay * Math.pow(2, attempt) * (0.5 + Math.random()),
            maxDelay
          );
          
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(attempt + 1);
        }
        throw error;
      }
    };

    return fetchWithRetry();
  }
});

// Connection state management
let isOnline = true;
let connectionCheckInterval: number | null = null;

// Update online status
function updateOnlineStatus(online: boolean) {
  if (isOnline !== online) {
    isOnline = online;
    if (online) {
      console.log('Connection restored');
    } else {
      console.warn('Connection lost');
    }
  }
}

// Start connection monitoring
export function startConnectionMonitoring() {
  // Listen for online/offline events
  window.addEventListener('online', () => updateOnlineStatus(true));
  window.addEventListener('offline', () => updateOnlineStatus(false));

  // Initial check
  updateOnlineStatus(navigator.onLine);

  // Periodic connection check
  if (!connectionCheckInterval) {
    connectionCheckInterval = window.setInterval(async () => {
      try {
        const response = await fetch(supabaseUrl, { 
          method: 'HEAD',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        updateOnlineStatus(response.ok);
      } catch {
        updateOnlineStatus(false);
      }
    }, 30000) as unknown as number;
  }
}

// Stop connection monitoring
export function stopConnectionMonitoring() {
  window.removeEventListener('online', () => updateOnlineStatus(true));
  window.removeEventListener('offline', () => updateOnlineStatus(false));
  if (connectionCheckInterval) {
    window.clearInterval(connectionCheckInterval);
    connectionCheckInterval = null;
  }
}

// Calculate delay with exponential backoff and jitter
function calculateRetryDelay(attempt: number): number {
  const exponentialDelay = Math.min(
    1000 * Math.pow(2, attempt),
    5000
  );
  // Add random jitter (±20%)
  const jitter = exponentialDelay * 0.2 * (Math.random() * 2 - 1);
  return Math.max(1000, exponentialDelay + jitter);
}

// Improved error handling helper with retries
export async function handleSupabaseError<T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  errorContext: string,
  retries = 3
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Check connection before attempt
      if (!isOnline) {
        console.warn('Offline - waiting for connection...');
        await new Promise<void>((resolve) => {
          const checkConnection = () => {
            if (isOnline) {
              resolve();
            } else {
              setTimeout(checkConnection, 1000);
            }
          };
          checkConnection();
        });
      }

      const { data, error } = await operation();
      
      if (error) {
        if ((error.message === 'Failed to fetch' || error.code === 'PGRST301') && attempt < retries) {
          lastError = error;
          const delay = calculateRetryDelay(attempt);
          console.warn(`Retrying ${errorContext} (${attempt + 1}/${retries}) after ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
      
      if (!data) {
        throw new Error('No data returned');
      }
      
      return data;
    } catch (error) {
      lastError = error;
      
      if (error instanceof Error && 
          (error.message === 'Failed to fetch' || 
           error.message.includes('network') || 
           error.message.includes('timeout')) && 
          attempt < retries) {
        const delay = calculateRetryDelay(attempt);
        console.warn(`Retrying ${errorContext} (${attempt + 1}/${retries}) after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      console.error(`Error in ${errorContext}:`, error);
      throw lastError;
    }
  }

  throw lastError;
}

// Add health check function with retries
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const result = await handleSupabaseError(
      () => supabase.from('profiles').select('count').limit(1),
      'connectionCheck'
    );
    return !!result;
  } catch {
    return false;
  }
}